import React, { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Home, Heart, Shield, Euro, Activity, FileText, Upload,
  FolderOpen, ChevronRight, Plus, Check, Clock, AlertTriangle,
  X, Edit, Save, ExternalLink, Building2, ArrowRight, Eye, RefreshCw
} from 'lucide-react';
import { formatWohnform, formatDatum } from '@/lib/formatUtils';
import { strasseZeile } from '@/lib/adresse';
import { normalisiereWohnform } from '@/lib/wohnform';
import WohnformFeld from '@/components/WohnformFeld';
import { useNavigate } from 'react-router-dom';
import { useTabs } from '@/contexts/TabContext';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// ===== Bereich-Definitionen =====
const BEREICHE = {
  wohnform: {
    label: 'Wohnform & Adresse',
    icon: Home,
    color: 'blue',
    kategorie: 'wohnform',
    felder: ['wohnform', 'adresse', 'plz', 'ort', 'einzug_datum'],
  },
  krankenversicherung: {
    label: 'Krankenversicherung',
    icon: Shield,
    color: 'emerald',
    kategorie: 'krankenversicherung',
    felder: ['krankenversicherung', 'kv_art', 'versichertennummer', 'kv_beitragszahler'],
    hatZuzahlungsbefreiung: true,
  },
  pflegegrad: {
    label: 'Pflegegrad & Behinderung',
    icon: Heart,
    color: 'rose',
    kategorie: 'pflegegrad',
    felder: ['pflegegrad', 'pflegestufe_seit', 'schwerbehinderung', 'grad_der_behinderung', 'merkzeichen'],
  },
  einnahmen: {
    label: 'Einnahmen & Finanzen',
    icon: Euro,
    color: 'amber',
    kategorie: 'einnahmen',
    felder: ['haupteinnahmequelle'],
  },
  versorgung: {
    label: 'Versorgung & Pflege',
    icon: Activity,
    color: 'purple',
    kategorie: 'versorgung',
    felder: [],
  },
};

// ===== Hauptkomponente: Klickbare Kacheln =====
export function LebenssituationKacheln({ klient, finanzen, betreuungLeistungen, einnahmenVorgaenge, klientId, onKlientUpdate }) {
  const [openBereich, setOpenBereich] = useState(null);

  const getKVStatus = () => {
    const kv = finanzen?.versicherungen?.find(v => v.typ === 'krankenversicherung');
    return kv?.bezeichnung || klient?.krankenversicherung || null;
  };

  const getZuzahlungStatus = () => {
    if (klient?.zuzahlungsbefreiung_status === 'befreit') return { text: 'Befreit', variant: 'default', color: 'bg-green-100 text-green-800' };
    if (klient?.zuzahlungsbefreiung_status === 'beantragt') return { text: 'Beantragt', variant: 'secondary', color: 'bg-yellow-100 text-yellow-800' };
    if (klient?.zuzahlungsbefreiung) return { text: 'Befreit', variant: 'default', color: 'bg-green-100 text-green-800' };
    return null;
  };

  const getVersorgungDisplay = () => {
    const aktive = (finanzen?.versorgung || []).filter(v => v.status === 'aktiv');
    return aktive.length > 0 ? aktive.map(v => v.bezeichnung || v.typ).join(', ') : null;
  };

  // Einnahmen: Vorgänge als Single Source of Truth
  const vorgaenge = einnahmenVorgaenge || [];
  const ersteEinnahme = vorgaenge.length > 0
    ? vorgaenge[0]?.typ_label || vorgaenge[0]?.bezeichnung
    : null;

  const kacheln = [
    {
      key: 'wohnform',
      // Wohnform und Anschrift getrennt: ohne erfasste Wohnform bleibt sie ausdruecklich offen (nie aus der Anschrift abgeleitet)
      wert: normalisiereWohnform(klient?.wohnform) ? formatWohnform(klient.wohnform) : null,
      leer: 'Wohnform offen – klären',
      subtext: [strasseZeile(klient), klient?.plz, klient?.ort].filter(Boolean).join(', ') || null,
    },
    {
      key: 'krankenversicherung',
      wert: getKVStatus(),
      subtext: klient?.kv_art === 'gkv' ? 'Gesetzlich' : klient?.kv_art === 'pkv' ? 'Privat' : klient?.kv_art || null,
      zuzahlung: getZuzahlungStatus(),
    },
    {
      key: 'pflegegrad',
      wert: klient?.pflegegrad ? `Pflegegrad ${klient.pflegegrad}` : null,
      subtext: klient?.schwerbehinderung ? `GdB ${klient?.grad_der_behinderung || '?'}%` : null,
    },
    {
      key: 'einnahmen',
      wert: klient?.haupteinnahmequelle
        || ersteEinnahme
        || (finanzen?.einnahmen?.length > 0 ? finanzen.einnahmen[0]?.bezeichnung || finanzen.einnahmen[0]?.typ || `${finanzen.einnahmen.length} Einnahmen` : null),
      subtext: finanzen?.statistik?.einnahmen_monatlich > 0
        ? `${finanzen.statistik.einnahmen_monatlich.toLocaleString('de-DE')} €/Monat`
        : vorgaenge.length > 0 ? `${vorgaenge.length} Posten erfasst` : null,
    },
    {
      key: 'versorgung',
      wert: getVersorgungDisplay(),
      subtext: null,
    },
  ];

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {kacheln.map(({ key, wert, leer, subtext, zuzahlung }) => {
          const def = BEREICHE[key];
          const Icon = def.icon;
          const colorMap = {
            blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50/50',
            emerald: 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50/50',
            rose: 'border-rose-200 hover:border-rose-400 hover:bg-rose-50/50',
            amber: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50/50',
            purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50/50',
          };
          const iconColorMap = {
            blue: 'text-blue-500', emerald: 'text-emerald-500', rose: 'text-rose-500',
            amber: 'text-amber-500', purple: 'text-purple-500',
          };

          return (
            <button
              key={key}
              data-testid={`lebenssituation-kachel-${key}`}
              onClick={() => setOpenBereich(key)}
              className={`group relative text-left p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${colorMap[def.color]} bg-white`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconColorMap[def.color]}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{def.label}</p>
                    <p className={`font-semibold mt-0.5 truncate ${wert ? 'text-slate-800' : 'text-red-500'}`}>
                      {wert || leer || 'Nicht erfasst'}
                    </p>
                    {subtext && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtext}</p>}
                    {zuzahlung && (
                      <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-1 ${zuzahlung.color}`}>
                        Zuzahlung: {zuzahlung.text}
                        {klient?.zuzahlungsbefreiung_bis && ` bis ${new Date(klient.zuzahlungsbefreiung_bis).toLocaleDateString('de-DE')}`}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" />
              </div>
            </button>
          );
        })}
      </div>

      {openBereich && (
        <BereichSheet
          bereichKey={openBereich}
          klient={klient}
          klientId={klientId}
          finanzen={finanzen}
          betreuungLeistungen={betreuungLeistungen}
          einnahmenVorgaenge={einnahmenVorgaenge}
          onClose={() => setOpenBereich(null)}
          onKlientUpdate={onKlientUpdate}
        />
      )}
    </>
  );
}

// ===== Sheet-Detail für einen Bereich =====
function BereichSheet({ bereichKey, klient, klientId, finanzen, betreuungLeistungen, einnahmenVorgaenge, onClose, onKlientUpdate }) {
  const [dokumente, setDokumente] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [kvInstitutionen, setKvInstitutionen] = useState([]);
  const def = BEREICHE[bereichKey];

  // Krankenversicherungen laden
  useEffect(() => {
    if (bereichKey === 'krankenversicherung') {
      fetch(`${API}/api/betreuung/juristische-personen?typ=krankenversicherung`)
        .then(r => r.json())
        .then(data => {
          const items = Array.isArray(data) ? data : data.personen || [];
          setKvInstitutionen(items.filter(i => i.status !== 'inaktiv'));
        })
        .catch(() => {});
    }
  }, [bereichKey]);

  const loadDokumente = useCallback(async () => {
    setLoadingDocs(true);
    try {
      if (bereichKey === 'einnahmen' && einnahmenVorgaenge?.length > 0) {
        // Einnahmen: Nur den neuesten Bescheid pro Vorgang anzeigen
        const bescheide = [];
        for (const v of einnahmenVorgaenge) {
          if (v.neuester_bescheid) {
            bescheide.push({
              ...v.neuester_bescheid,
              vorgang_label: v.typ_label || v.bezeichnung,
              vorgang_id: v.id,
              vorgang_betrag: v.betrag,
            });
          }
        }
        setDokumente(bescheide);
      } else {
        const res = await fetch(`${API}/api/betreuung/dokumente?klient_id=${klientId}&dokument_kategorie=${def.kategorie}`);
        if (res.ok) {
          const data = await res.json();
          setDokumente(Array.isArray(data) ? data : data.dokumente || []);
        }
      }
    } catch (e) {
      console.error('Dokumente laden fehlgeschlagen:', e);
    }
    setLoadingDocs(false);
  }, [klientId, def.kategorie, bereichKey, einnahmenVorgaenge]);

  useEffect(() => {
    loadDokumente();
  }, [loadDokumente]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/betreuung/klienten/${klientId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      if (res.ok) {
        const updated = await res.json();
        onKlientUpdate?.(updated);
        setEditMode(false);
      }
    } catch (e) {
      console.error('Speichern fehlgeschlagen:', e);
    }
    setSaving(false);
  };

  const handleAssignDoc = async (docId) => {
    try {
      await fetch(`${API}/api/betreuung/dokumente/${docId}/kategorie`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dokument_kategorie: def.kategorie }),
      });
      loadDokumente();
    } catch (e) {
      console.error('Kategorie-Zuweisung fehlgeschlagen:', e);
    }
  };

  const Icon = def.icon;

  return (
    <Sheet open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid={`bereich-sheet-${bereichKey}`}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {def.label}
          </SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="details" className="mt-4">
          <TabsList className="w-full">
            <TabsTrigger value="details" className="flex-1" data-testid={`bereich-tab-details-${bereichKey}`}>Details</TabsTrigger>
            <TabsTrigger value="dokumente" className="flex-1" data-testid={`bereich-tab-dokumente-${bereichKey}`}>
              Dokumente ({dokumente.length})
            </TabsTrigger>
            {def.hatZuzahlungsbefreiung && (
              <TabsTrigger value="zuzahlung" className="flex-1" data-testid="bereich-tab-zuzahlung">
                Zuzahlung
              </TabsTrigger>
            )}
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-4 mt-4">
            <BereichDetails
              bereichKey={bereichKey}
              klient={klient}
              finanzen={finanzen}
              betreuungLeistungen={betreuungLeistungen}
              einnahmenVorgaenge={einnahmenVorgaenge}
              editMode={editMode}
              editData={editData}
              setEditData={setEditData}
              kvInstitutionen={kvInstitutionen}
              onNavigateAway={onClose}
            />
            <div className="flex gap-2 pt-2">
              {editMode ? (
                <>
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid="bereich-save-btn">
                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Speichere...' : 'Speichern'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>Abbrechen</Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => { setEditMode(true); setEditData({}); }} data-testid="bereich-edit-btn">
                  <Edit className="w-4 h-4 mr-1" /> Bearbeiten
                </Button>
              )}
            </div>
          </TabsContent>

          {/* Dokumente Tab */}
          <TabsContent value="dokumente" className="mt-4">
            <BereichDokumente
              dokumente={dokumente}
              loading={loadingDocs}
              klientId={klientId}
              kategorie={def.kategorie}
              onRefresh={loadDokumente}
              bereichKey={bereichKey}
              einnahmenVorgaenge={einnahmenVorgaenge}
            />
          </TabsContent>

          {/* Zuzahlungsbefreiung Tab (nur bei KV) */}
          {def.hatZuzahlungsbefreiung && (
            <TabsContent value="zuzahlung" className="mt-4">
              <ZuzahlungsbefreiungPanel klient={klient} klientId={klientId} onKlientUpdate={onKlientUpdate} />
            </TabsContent>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// ===== Detail-Ansicht pro Bereich =====
function BereichDetails({ bereichKey, klient, finanzen, betreuungLeistungen, einnahmenVorgaenge, editMode, editData, setEditData, kvInstitutionen, onNavigateAway }) {
  const [kvSearch, setKvSearch] = useState('');
  const [kvDropdownOpen, setKvDropdownOpen] = useState(false);

  const feld = (label, key, fallback) => {
    let val = klient?.[key];
    if (key === 'adresse' && !val) val = strasseZeile(klient);
    // Werte formatieren
    if (key === 'wohnform' && val) val = formatWohnform(val);
    if ((key.includes('datum') || key.includes('_seit') || key.includes('_bis')) && val) val = formatDatum(val);
    
    if (editMode) {
      return (
        <div key={key}>
          <label className="text-xs text-slate-500">{label}</label>
          <Input
            className="mt-1"
            defaultValue={klient?.[key] || ''}
            onChange={(e) => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
            data-testid={`bereich-input-${key}`}
          />
        </div>
      );
    }
    return (
      <div key={key} data-testid={`bereich-feld-${key}`}>
        <p className="text-xs text-slate-400">{label}</p>
        <p className={`font-medium ${val ? 'text-slate-800' : 'text-slate-400'}`}>{val || fallback || 'Nicht erfasst'}</p>
      </div>
    );
  };

  switch (bereichKey) {
    case 'wohnform':
      return (
        <div className="space-y-3">
          <WohnformFeld klient={klient} editMode={editMode} editData={editData} setEditData={setEditData} />
          {feld('Straße', 'adresse')}
          <div className="grid grid-cols-2 gap-3">
            {feld('PLZ', 'plz')}
            {feld('Ort', 'ort')}
          </div>
          {feld('Einzugsdatum', 'einzug_datum')}
        </div>
      );

    case 'krankenversicherung': {
      const currentKV = editData.krankenversicherung ?? klient?.krankenversicherung ?? '';
      const filteredKV = (kvInstitutionen || []).filter(inst =>
        !kvSearch || inst.name.toLowerCase().includes(kvSearch.toLowerCase())
      );

      return (
        <div className="space-y-3">
          {editMode ? (
            <div>
              <label className="text-xs text-slate-500">Krankenversicherung</label>
              <div className="relative mt-1">
                <Input
                  value={kvSearch || currentKV}
                  onChange={(e) => {
                    setKvSearch(e.target.value);
                    setEditData(prev => ({ ...prev, krankenversicherung: e.target.value }));
                    setKvDropdownOpen(true);
                  }}
                  onFocus={() => setKvDropdownOpen(true)}
                  placeholder="Krankenversicherung suchen oder eingeben..."
                  data-testid="bereich-input-krankenversicherung"
                />
                {kvDropdownOpen && filteredKV.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredKV.map(inst => (
                      <button
                        key={inst.id}
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between ${
                          currentKV === inst.name ? 'bg-blue-50 font-medium' : ''
                        }`}
                        onClick={() => {
                          setEditData(prev => ({
                            ...prev,
                            krankenversicherung: inst.name,
                            krankenversicherung_id: inst.id,
                          }));
                          setKvSearch(inst.name);
                          setKvDropdownOpen(false);
                        }}
                        data-testid={`kv-option-${inst.id}`}
                      >
                        <span>{inst.name}</span>
                        {inst.adresse && <span className="text-xs text-slate-400 ml-2">{inst.adresse}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {kvInstitutionen?.length === 0 && (
                <p className="text-xs text-slate-400 mt-1">Keine Krankenversicherungen als juristische Person erfasst. Freitexteingabe möglich.</p>
              )}
            </div>
          ) : (
            feld('Krankenversicherung', 'krankenversicherung')
          )}
          {editMode ? (
            <div>
              <label className="text-xs text-slate-500">Art</label>
              <select
                className="w-full mt-1 border rounded-lg p-2 text-sm"
                defaultValue={klient?.kv_art || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, kv_art: e.target.value }))}
                data-testid="bereich-input-kv_art"
              >
                <option value="">Bitte wählen</option>
                <option value="gkv">Gesetzlich (GKV)</option>
                <option value="pkv">Privat (PKV)</option>
                <option value="fgkv">Freiwillig gesetzlich</option>
              </select>
            </div>
          ) : (
            feld('Art', 'kv_art', 'k.A.')
          )}
          {feld('Versichertennummer', 'versichertennummer')}
          {feld('Beitragszahler', 'kv_beitragszahler')}
        </div>
      );
    }

    case 'pflegegrad':
      return (
        <div className="space-y-3">
          {feld('Pflegegrad', 'pflegegrad', 'Keiner')}
          {feld('Seit', 'pflegestufe_seit')}
          {feld('Schwerbehinderung', 'schwerbehinderung', 'Nein')}
          {feld('Grad der Behinderung', 'grad_der_behinderung')}
          {feld('Merkzeichen', 'merkzeichen')}
        </div>
      );

    case 'einnahmen': {
      const vorgaenge = einnahmenVorgaenge || [];
      return (
        <div className="space-y-3">
          {feld('Haupteinnahmequelle', 'haupteinnahmequelle')}
          {finanzen?.statistik?.einnahmen_monatlich > 0 && (
            <div>
              <p className="text-xs text-slate-400">Monatliche Einnahmen</p>
              <p className="font-bold text-lg text-emerald-600">{finanzen.statistik.einnahmen_monatlich.toLocaleString('de-DE')} €</p>
            </div>
          )}
          {vorgaenge.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 mb-2">Sozialleistungen</p>
              {vorgaenge.map((v, i) => (
                <VorgangEinnahmeItem key={v.id || i} vorgang={v} klientId={klient?.id} onNavigateAway={onNavigateAway} />
              ))}
            </div>
          )}
        </div>
      );
    }

    case 'versorgung': {
      const aktive = (finanzen?.versorgung || []).filter(v => v.status === 'aktiv');
      return (
        <div className="space-y-3">
          {aktive.length > 0 ? (
            aktive.map((v, i) => (
              <Card key={i} className="border">
                <CardContent className="p-3">
                  <p className="font-medium">{v.bezeichnung || v.typ}</p>
                  {v.anbieter && <p className="text-xs text-slate-500">{v.anbieter}</p>}
                  {v.betrag > 0 && <p className="text-sm font-medium mt-1">{v.betrag.toLocaleString('de-DE')} €/Monat</p>}
                </CardContent>
              </Card>
            ))
          ) : (
            <p className="text-slate-400 text-sm text-center py-6">Keine aktive Versorgung erfasst</p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

// ===== Dokumente-Ansicht pro Bereich =====
function BereichDokumente({ dokumente, loading, klientId, kategorie, onRefresh, bereichKey, einnahmenVorgaenge }) {
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [changingVorgang, setChangingVorgang] = useState(null);
  const [allKlientDocs, setAllKlientDocs] = useState([]);
  const [loadingAllDocs, setLoadingAllDocs] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('datei', file);
      formData.append('klient_id', klientId);
      formData.append('dokument_kategorie', kategorie);
      const res = await fetch(`${API}/api/betreuung/dokumente/upload`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) onRefresh();
    } catch (e) {
      console.error('Upload fehlgeschlagen:', e);
    }
    setUploading(false);
    e.target.value = '';
  };

  // Alle Dokumente des Klienten laden für die Ändern-Funktion
  const loadAllDocs = async () => {
    setLoadingAllDocs(true);
    try {
      const res = await fetch(`${API}/api/betreuung/dokumente?klient_id=${klientId}`);
      if (res.ok) {
        const data = await res.json();
        setAllKlientDocs(Array.isArray(data) ? data : data.dokumente || []);
      }
    } catch {}
    setLoadingAllDocs(false);
  };

  const handleChangeBescheid = async (vorgangId, dokumentId) => {
    try {
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgangId}/dokumente/zuordnen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dokument_id: dokumentId }),
      });
      if (res.ok || res.status === 409) {
        setChangingVorgang(null);
        onRefresh();
      }
    } catch (e) {
      console.error('Zuordnung fehlgeschlagen:', e);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-400">Lade Dokumente...</div>;
  }

  // Spezielle Einnahmen-Ansicht: Pro Vorgang den aktuellen Bescheid
  if (bereichKey === 'einnahmen' && einnahmenVorgaenge?.length > 0) {
    return (
      <div className="space-y-3" data-testid="einnahmen-dokumente">
        <p className="text-sm font-medium text-slate-600">Aktuelle Bescheide</p>
        <p className="text-xs text-slate-400">Pro Einnahme der aktuellste Nachweis über die Zahlungshöhe</p>

        {einnahmenVorgaenge.map((v, i) => {
          const bescheid = v.neuester_bescheid;
          return (
            <Card key={v.id || i} className="border border-slate-200 hover:border-slate-300 transition-colors">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    {v.typ_label || v.bezeichnung}
                  </span>
                  {v.betrag != null && (
                    <Badge variant="outline" className="text-xs font-mono">
                      {Number(v.betrag).toLocaleString('de-DE')} € / {v.turnus || 'mtl.'}
                    </Badge>
                  )}
                </div>
                
                {bescheid ? (
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      onClick={() => setPreviewDoc(bescheid)}
                      className="flex items-center gap-2 flex-1 min-w-0 p-2 rounded-lg bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-200 transition-colors cursor-pointer text-left"
                      data-testid={`bescheid-preview-${v.typ || i}`}
                    >
                      <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-700 truncate">{bescheid.dateiname || bescheid.titel || 'Bescheid'}</p>
                        <p className="text-xs text-slate-400">
                          {bescheid.created_date && new Date(bescheid.created_date).toLocaleDateString('de-DE')}
                          {' · Klicken für Vorschau'}
                        </p>
                      </div>
                      <Eye className="w-4 h-4 text-slate-300 shrink-0" />
                    </button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0"
                      onClick={() => { setChangingVorgang(v); loadAllDocs(); }}
                      data-testid={`bescheid-change-${v.typ || i}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 p-2 rounded-lg bg-amber-50 border border-amber-100 text-center">
                      <p className="text-xs text-amber-600">Kein Bescheid zugeordnet</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={() => { setChangingVorgang(v); loadAllDocs(); }}
                      data-testid={`bescheid-assign-${v.typ || i}`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Zuordnen
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Vorschau-Dialog */}
        {previewDoc && (
          <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewDoc(null)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{previewDoc.dateiname || previewDoc.titel || 'Dokument'}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{previewDoc.created_date && new Date(previewDoc.created_date).toLocaleDateString('de-DE')}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(null)} data-testid="preview-close">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex-1 min-h-0 p-1">
                <iframe
                  src={`${API}/api/betreuung/dokumente/${previewDoc.id}/download?inline=true`}
                  className="w-full h-full min-h-[70vh] rounded"
                  title="Dokumenten-Vorschau"
                />
              </div>
            </div>
          </div>
        )}

        {/* Dokument-Ändern-Dialog */}
        {changingVorgang && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setChangingVorgang(null)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-4 border-b">
                <p className="font-semibold text-slate-800">Bescheid zuordnen</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Wählen Sie das Dokument für &quot;{changingVorgang.typ_label || changingVorgang.bezeichnung}&quot;
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-1.5">
                {loadingAllDocs ? (
                  <p className="text-sm text-slate-400 text-center py-4">Lade Dokumente...</p>
                ) : allKlientDocs.length > 0 ? (
                  allKlientDocs.map((doc, i) => (
                    <button
                      key={doc.id || i}
                      onClick={() => handleChangeBescheid(changingVorgang.id, doc.id)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-blue-50 hover:border-blue-200 transition-colors text-left"
                      data-testid={`select-doc-${i}`}
                    >
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">{doc.dateiname || doc.name || doc.titel}</p>
                        <p className="text-xs text-slate-400">
                          {doc.dokumententyp || doc.typ || doc.dokument_kategorie || 'Dokument'}
                          {doc.erstellt_am && ` · ${new Date(doc.erstellt_am).toLocaleDateString('de-DE')}`}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Keine Dokumente verfügbar</p>
                )}
              </div>
              <div className="p-4 border-t flex justify-end">
                <Button size="sm" variant="ghost" onClick={() => setChangingVorgang(null)}>Abbrechen</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Standard-Ansicht für andere Bereiche
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-600">{dokumente.length} Dokumente</p>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.jpg,.png" />
          <Button size="sm" variant="outline" disabled={uploading} asChild>
            <span data-testid={`bereich-upload-${kategorie}`}>
              <Upload className="w-4 h-4 mr-1" />
              {uploading ? 'Lade...' : 'Hochladen'}
            </span>
          </Button>
        </label>
      </div>

      {dokumente.length > 0 ? (
        <div className="space-y-1.5">
          {dokumente.map((doc, i) => (
            <button
              key={doc.id || i}
              onClick={() => setPreviewDoc(doc)}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-blue-50 hover:border-blue-200 transition-colors group text-left"
              data-testid={`bereich-dokument-${i}`}
            >
              <FileText className="w-4 h-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-700 truncate">{doc.dateiname || doc.name}</p>
                <p className="text-xs text-slate-400">
                  {doc.dokumententyp || doc.typ || 'Dokument'}
                  {doc.erstellt_am && ` · ${new Date(doc.erstellt_am).toLocaleDateString('de-DE')}`}
                </p>
              </div>
              <Eye className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-500 shrink-0" />
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <FolderOpen className="w-10 h-10 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Keine Dokumente in dieser Kategorie</p>
          <p className="text-xs text-slate-300 mt-1">Dokumente hochladen oder per MCP zuweisen</p>
        </div>
      )}

      {/* Vorschau-Dialog (Standard-Bereiche) */}
      {previewDoc && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 truncate">{previewDoc.dateiname || previewDoc.titel || 'Dokument'}</p>
                <p className="text-xs text-slate-400 mt-0.5">{previewDoc.erstellt_am && new Date(previewDoc.erstellt_am).toLocaleDateString('de-DE')}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setPreviewDoc(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 min-h-0 p-1">
              <iframe
                src={`${API}/api/betreuung/dokumente/${previewDoc.id}/download?inline=true`}
                className="w-full h-full min-h-[70vh] rounded"
                title="Dokumenten-Vorschau"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Zuzahlungsbefreiung Panel =====
function ZuzahlungsbefreiungPanel({ klient, klientId, onKlientUpdate }) {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    zuzahlungsbefreiung: klient?.zuzahlungsbefreiung || false,
    zuzahlungsbefreiung_status: klient?.zuzahlungsbefreiung_status || '',
    zuzahlungsbefreiung_bis: klient?.zuzahlungsbefreiung_bis || '',
    zuzahlungsbefreiung_belastungsgrenze: klient?.zuzahlungsbefreiung_belastungsgrenze || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/betreuung/klienten/${klientId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        const updated = await res.json();
        onKlientUpdate?.(updated);
        setEditMode(false);
      }
    } catch (e) {
      console.error('Speichern fehlgeschlagen:', e);
    }
    setSaving(false);
  };

  const status = klient?.zuzahlungsbefreiung_status;
  const befreit = status === 'befreit' || klient?.zuzahlungsbefreiung;

  return (
    <div className="space-y-4" data-testid="zuzahlungsbefreiung-panel">
      <Card className={`border-2 ${befreit ? 'border-green-200 bg-green-50/50' : status === 'beantragt' ? 'border-yellow-200 bg-yellow-50/50' : 'border-slate-200'}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            {befreit ? (
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
            ) : status === 'beantragt' ? (
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-slate-400" />
              </div>
            )}
            <div>
              <p className="font-semibold text-lg">
                {befreit ? 'Zuzahlungsbefreit' : status === 'beantragt' ? 'Befreiung beantragt' : 'Nicht befreit'}
              </p>
              {klient?.zuzahlungsbefreiung_bis && (
                <p className="text-sm text-slate-500">Gültig bis: {new Date(klient.zuzahlungsbefreiung_bis).toLocaleDateString('de-DE')}</p>
              )}
              {klient?.zuzahlungsbefreiung_belastungsgrenze && (
                <p className="text-sm text-slate-500">Belastungsgrenze: {klient.zuzahlungsbefreiung_belastungsgrenze} €</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {editMode ? (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <select
              className="w-full mt-1 border rounded-lg p-2 text-sm"
              value={formData.zuzahlungsbefreiung_status}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                zuzahlungsbefreiung_status: e.target.value,
                zuzahlungsbefreiung: e.target.value === 'befreit',
              }))}
              data-testid="zuzahlung-status-select"
            >
              <option value="">Nicht beantragt</option>
              <option value="beantragt">Beantragt</option>
              <option value="befreit">Befreit</option>
              <option value="abgelehnt">Abgelehnt</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Gültig bis</label>
            <Input
              type="date"
              value={formData.zuzahlungsbefreiung_bis || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, zuzahlungsbefreiung_bis: e.target.value }))}
              data-testid="zuzahlung-bis-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Belastungsgrenze (€)</label>
            <Input
              type="number"
              value={formData.zuzahlungsbefreiung_belastungsgrenze || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, zuzahlungsbefreiung_belastungsgrenze: e.target.value }))}
              data-testid="zuzahlung-grenze-input"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="zuzahlung-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Speichere...' : 'Speichern'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>Abbrechen</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setEditMode(true)} data-testid="zuzahlung-edit-btn">
          <Edit className="w-4 h-4 mr-1" /> Bearbeiten
        </Button>
      )}
    </div>
  );
}


// ===== Klickbare Einnahmen-Zeile mit Drill-Down =====
function VorgangEinnahmeItem({ vorgang, klientId, onNavigateAway }) {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const [expanded, setExpanded] = useState(false);
  const [dokumente, setDokumente] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const v = vorgang;

  const goTo = (path, deepLinkData) => {
    if (onNavigateAway) onNavigateAway();
    if (deepLinkData) {
      localStorage.setItem('vorgaenge_deep_link', JSON.stringify(deepLinkData));
    }
    setTimeout(() => {
      openTab(path, { title: 'Vorgänge' });
      navigate(path);
      if (deepLinkData) {
        window.dispatchEvent(new CustomEvent('vorgaenge-deep-link'));
      }
    }, 150);
  };

  const loadDocs = async () => {
    if (dokumente.length > 0) return;
    setLoadingDocs(true);
    try {
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${v.id}/dokumente`);
      if (res.ok) setDokumente(await res.json());
    } catch {}
    setLoadingDocs(false);
  };

  const handleClick = () => {
    // Direkt zur klient-spezifischen Vorgang-Detailseite navigieren
    const path = `/akten/${klientId}/vorgang/${v.id}`;
    openTab(path, { title: v.typ_label || v.bezeichnung });
    navigate(path);
  };

  return (
    <div className="border-b border-slate-100 last:border-0" data-testid={`einnahme-item-${v.typ || v.id}`}>
      {/* Zeile – klickbar */}
      <button
        onClick={handleClick}
        className="w-full flex items-center justify-between text-sm py-2 hover:bg-slate-50 transition-colors rounded -mx-1 px-1"
        data-testid={`einnahme-click-${v.typ || v.id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${expanded ? 'rotate-90' : ''}`} />
          <span className="truncate font-medium">{v.typ_label || v.bezeichnung}</span>
          {v.institution && <span className="text-xs text-slate-400 truncate">({v.institution})</span>}
          {v.dokumente_count > 0 && (
            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full shrink-0">{v.dokumente_count} Dok.</span>
          )}
        </div>
        <span className="font-medium shrink-0 ml-2">{v.betrag != null ? `${Number(v.betrag).toLocaleString('de-DE')} €` : '–'} / {v.turnus || 'mtl.'}</span>
      </button>

      {/* Detail-Panel */}
      {expanded && (
        <div className="ml-5 pb-3 space-y-2.5 text-sm" data-testid={`einnahme-detail-${v.typ || v.id}`}>
          {/* Felder */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {v.institution && (
              <div>
                <p className="text-slate-400">Träger</p>
                <button
                  onClick={() => goTo('/vorgaenge', { traeger: v.institution })}
                  className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                  data-testid={`einnahme-goto-traeger-${v.typ}`}
                >
                  <Building2 className="w-3 h-3" /> {v.institution}
                </button>
              </div>
            )}
            {v.aktenzeichen && (
              <div>
                <p className="text-slate-400">Aktenzeichen</p>
                <p className="font-mono text-xs">{v.aktenzeichen}</p>
              </div>
            )}
          </div>

          {/* Dokumente */}
          {loadingDocs ? (
            <p className="text-xs text-slate-400">Lade Dokumente...</p>
          ) : dokumente.length > 0 ? (
            <div>
              <p className="text-xs text-slate-400 mb-1">Verknüpfte Dokumente</p>
              {dokumente.slice(0, 3).map((doc, i) => (
                <div key={doc.id || i} className="flex items-center gap-2 py-1 text-xs">
                  <FileText className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="truncate text-slate-700">{doc.dateiname || doc.titel || 'Dokument'}</span>
                  {doc.created_date && <span className="text-slate-300 shrink-0">{new Date(doc.created_date).toLocaleDateString('de-DE')}</span>}
                </div>
              ))}
              {dokumente.length > 3 && (
                <p className="text-xs text-slate-400">+ {dokumente.length - 3} weitere</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Keine Dokumente verknüpft</p>
          )}

          {/* Navigationslinks */}
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => goTo('/vorgaenge', { typ: v.typ, typLabel: v.typ_label || v.bezeichnung })}
              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              data-testid={`einnahme-alle-typ-${v.typ}`}
            >
              Alle {v.typ_label || v.bezeichnung} anzeigen <ArrowRight className="w-3 h-3" />
            </button>
            {v.institution && (
              <button
                onClick={() => goTo('/vorgaenge', { traeger: v.institution })}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                data-testid={`einnahme-alle-traeger-${v.typ}`}
              >
                Alle Vorgänge von {v.institution} <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LebenssituationKacheln;
